import {afterNextRender, Component, ElementRef, inject, input, OnDestroy, signal, viewChild} from '@angular/core';
import {Location} from "@angular/common";
import {NavigationExtras, Router} from "@angular/router";
import {CardModule} from "primeng/card";
import {ButtonModule} from "primeng/button";
import {TooltipModule} from "primeng/tooltip";

/**
 * A reusable layout component for detail views in the application.
 * Provides a structured layout with header, actions, and body sections.
 *
 * This component supports:
 * - Optional breadcrumbs navigation
 * - Optional back navigation button
 * - Content projection for header, actions, and body sections
 *
 * @example
 * ```html
 * <du-config-detail [navigateBack]="{commands: ['/systems']}" [disableBreadcrumbs]="false">
 *   <detail-header>
 *     <h1>System Details</h1>
 *   </detail-header>
 *   <detail-header-action>
 *     <button pButton>Save</button>
 *   </detail-header-action>
 *   <detail-body>
 *     <p>System information goes here</p>
 *   </detail-body>
 * </du-config-detail>
 * ```
 *
 * @component
 * @selector du-config-detail
 * @standalone
 *
 * @childElements
 * The following child elements can be projected into this component:
 * - `detail-header`: Content for the header section (title, subtitle, etc.)
 * - `detail-header-action`: Action buttons and controls for the header
 * - `detail-body`: Main content area for forms, tables, and other content
 */
@Component({
    selector: 'du-config-detail',
    imports: [
        CardModule,
        ButtonModule,
        TooltipModule
    ],
    templateUrl: './config-detail.component.html',
    standalone: true,
    styleUrl: './config-detail.component.scss'
})
export class ConfigDetailComponent implements OnDestroy {
  navigateBack = input<{ commands: any[], extras?: NavigationExtras } | undefined>();

  private readonly sentinel = viewChild.required<ElementRef>('sentinel');
  protected isStuck = signal(false);
  private observer: IntersectionObserver | undefined;

  private readonly router = inject(Router)
  private readonly location = inject(Location);

  constructor() {
    afterNextRender(() => {
      this.initObserver();
    });
  }

  private initObserver() {
    this.observer = new IntersectionObserver(
        ([entry]) => {
          this.isStuck.set(!entry.isIntersecting);
        },
        { threshold: 1 } // Trigger exactly when it leaves view
    );

    if (this.sentinel()) {
      this.observer.observe(this.sentinel().nativeElement);
    }
  }

  async handleNavigateBack() {
    const nav = this.navigateBack();
    if (nav) {
      await this.router.navigate(nav.commands, nav.extras);
    } else {
      this.location.back();
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
